/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {MaintenanceServiceItem} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_MAINTENANCE_SERVICE,
  ADD_ADMIN_MAINTENANCE_SERVICE_ERROR,
  ADD_ADMIN_MAINTENANCE_SERVICE_REQUEST,
  ADD_ADMIN_MAINTENANCE_SERVICE_RESPONSE,
  ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED,
  DELETE_ADMIN_MAINTENANCE_SERVICE,
  DELETE_ADMIN_MAINTENANCE_SERVICE_ERROR,
  DELETE_ADMIN_MAINTENANCE_SERVICE_REQUEST,
  DELETE_ADMIN_MAINTENANCE_SERVICE_RESPONSE,
  EDIT_ADMIN_MAINTENANCE_SERVICE,
  EDIT_ADMIN_MAINTENANCE_SERVICE_ERROR,
  EDIT_ADMIN_MAINTENANCE_SERVICE_REQUEST,
  EDIT_ADMIN_MAINTENANCE_SERVICE_RESPONSE,
  GET_ADMIN_MAINTENANCE_SERVICES_ERROR,
  GET_ADMIN_MAINTENANCE_SERVICES_REQUEST,
  GET_ADMIN_MAINTENANCE_SERVICES_RESPONSE,
  INITIALIZE_ADMIN_MAINTENANCE_SERVICES,
  RE_INITIALIZE_ADMIN_MAINTENANCE_SERVICES,
  SET_ADMIN_MAINTENANCE_SERVICES_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-maintenance-services';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminMaintenanceServiceAction extends AdminAction {
  service?: MaintenanceServiceItem;
  services?: MaintenanceServiceItem[];
};

interface AdminMaintenanceServicesActionState extends AsyncState {
  selected: MaintenanceServiceItem | MaintenanceServiceItem[] | null;
  result: MaintenanceServiceItem | MaintenanceServiceItem[] | null;
  isEditing: boolean;
};

export interface AdminMaintenanceServicesState extends PageState {
  services: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  add: AdminMaintenanceServicesActionState,
  edit: AdminMaintenanceServicesActionState,
  destroy: AdminMaintenanceServicesActionState
}

export interface AdminMaintenanceServicesAction extends AdminAction {
  service?: MaintenanceServiceItem;
  services?: MaintenanceServiceItem[];
};
// defines the user preference key for the admin maintenance services page ...
const PREFERENCE = 'admin-maintenance-services';

// defines the initial state for the reducer ...
export const initialState: AdminMaintenanceServicesState = {
  isInitialized : false,
  services      : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_MAINTENANCE_SERVICES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_MAINTENANCE_SERVICES] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, services}: AdminMaintenanceServicesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : services.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminMaintenanceServicesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_ADMIN_MAINTENANCE_SERVICES_REQUEST] : ({services}: AdminMaintenanceServicesState) => {
    services.loading = true;
  },
  [GET_ADMIN_MAINTENANCE_SERVICES_RESPONSE] : ({services}: AdminMaintenanceServicesState) => {
    services.loading = false;
  },
  [GET_ADMIN_MAINTENANCE_SERVICES_ERROR] : (
    {services}: AdminMaintenanceServicesState, {error}: AdminMaintenanceServicesAction
  ) => {
    services.loading = false;
    services.error   = error;
  },

  [ADD_ADMIN_MAINTENANCE_SERVICE] : (
    {add}: AdminMaintenanceServicesState, {show}: AdminMaintenanceServiceAction
  ) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_MAINTENANCE_SERVICE_REQUEST] : ({add}: AdminMaintenanceServicesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_MAINTENANCE_SERVICE_RESPONSE] : (
    {add}: AdminMaintenanceServicesState, {service}: AdminMaintenanceServiceAction
  ) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = service;
  },
  [ADD_ADMIN_MAINTENANCE_SERVICE_ERROR] : (
    {add}: AdminMaintenanceServicesState, {error}: AdminMaintenanceServiceAction
  ) => {
    add.loading   = false;
    add.error     = error;
  },

  [EDIT_ADMIN_MAINTENANCE_SERVICE] : (
    {edit}: AdminMaintenanceServicesState, {service, show}: AdminMaintenanceServiceAction
  ) => {
    edit.selected  = show ? service : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_MAINTENANCE_SERVICE_REQUEST] : ({edit}: AdminMaintenanceServicesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_MAINTENANCE_SERVICE_RESPONSE] : (
    {edit}: AdminMaintenanceServicesState, {service}: AdminMaintenanceServiceAction
  ) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = service;
  },
  [EDIT_ADMIN_MAINTENANCE_SERVICE_ERROR] : (
    {edit}: AdminMaintenanceServicesState, {error}: AdminMaintenanceServiceAction
  ) => {
    edit.loading   = false;
    edit.error     = error;
  },

  [DELETE_ADMIN_MAINTENANCE_SERVICE] : (
    {destroy}: AdminMaintenanceServicesState, {service, show}: AdminMaintenanceServicesAction
  ) => {
    destroy.selected  = show ? service : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_MAINTENANCE_SERVICE_REQUEST] : ({destroy}: AdminMaintenanceServicesState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_MAINTENANCE_SERVICE_RESPONSE] : (
    {destroy}: AdminMaintenanceServicesState, {service}: AdminMaintenanceServicesAction
  ) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = service;
  },
  [DELETE_ADMIN_MAINTENANCE_SERVICE_ERROR] : (
    {destroy}: AdminMaintenanceServicesState, {error}: AdminMaintenanceServicesAction) => {
    destroy.loading   = false;
    destroy.error     = error;
    destroy.result    = null;
  },
  [ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED] : (
    {add, edit, destroy}: AdminMaintenanceServicesState
  ) => {
    add.result        = null;
    edit.result       = null;
    destroy.result    = null;
  },
  [SET_ADMIN_MAINTENANCE_SERVICES_GRID_FILTER_MODEL] : ({grid}: AdminMaintenanceServicesState, {model}) => {
    grid.filterModel = model;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminMaintenanceServicesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
